/**
 * Step 3 sub — Google Ads account picker.
 *
 * 2026-05-22 redesign:
 *   - Wraps with OnboardingShell (stepper + rail)
 *   - Selection state: dark/40 border + shadow-card elevation + green
 *     check icon (the restrained luxurious pattern from the preview)
 *   - Coral kept only on the bottom Continue CTA
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, AlertCircle } from 'lucide-react';

import { useAuth } from '@/lib/useAuth';
import { apiRequest, ApiError } from '@/lib/api';
import { computeRailState } from '@/lib/onboardingState';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { cn } from '@/lib/utils';

interface AccountInfo {
  customer_id: string;
  name: string;
  currency: string | null;
  status: string | null;
}

interface AccountsResponse {
  accounts: AccountInfo[];
  mcc_only: boolean;
}

export function OnboardingGoogleAdsPickPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [accounts, setAccounts] = useState<AccountInfo[] | null>(null);
  const [mccOnly, setMccOnly] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (me?.google_ads_customer_id) {
      navigate('/onboarding/connect/google-setup', { replace: true });
    }
  }, [me?.google_ads_customer_id, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest<AccountsResponse>('/oauth/google-ads/accounts');
        if (cancelled) return;
        setAccounts(res.accounts);
        setMccOnly(res.mcc_only);
        if (res.accounts.length === 1) {
          setSelected(res.accounts[0].customer_id);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 400) {
          const code = e.detail ?? '';
          const search = code === 'no_ads_user' ? '?google_error=no_ads_user' : '';
          navigate(`/onboarding/connect${search}`, { replace: true });
          return;
        }
        setLoadError("We couldn't load your Google Ads accounts. Try again or reconnect.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function handleContinue() {
    if (!selected || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiRequest('/oauth/google-ads/select-account', {
        method: 'POST',
        body: { customer_id: selected },
      });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/onboarding/connect/google-setup', { replace: true });
    } catch (e) {
      setSubmitError(
        e instanceof ApiError && e.detail
          ? e.detail
          : "We couldn't save your selection. Please try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <OnboardingShell
      current={3}
      title="Pick your Google Ads account"
      subtitle="Choose which account HeyMotek pushes offline conversions to. We'll create three conversion actions on it."
      state={computeRailState(me ?? null)}
    >
      <div className="space-y-5">
        {accounts === null && !loadError && (
          <div className="flex items-center gap-3 rounded-2xl border bg-muted/30 p-3 text-sm">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading your accounts…</span>
          </div>
        )}

        {loadError && (
          <div className="space-y-3">
            <Banner variant="error">
              <span>{loadError}</span>
            </Banner>
            <Button type="button" variant="outline" onClick={() => navigate('/onboarding/connect', { replace: true })}>
              Back to Connect
            </Button>
          </div>
        )}

        {accounts !== null && mccOnly && (
          <div className="space-y-3">
            <Banner variant="warn">
              <strong className="font-semibold">Manager-account login.</strong>{' '}
              You signed in via a Google Ads manager (MCC). HeyMotek connects to a direct-admin
              account, not a manager. Sign out, sign in with the Google account that owns the
              actual Ads account, then click Connect again.
            </Banner>
            <Button type="button" variant="outline" onClick={() => navigate('/onboarding/connect', { replace: true })}>
              Back to Connect
            </Button>
          </div>
        )}

        {accounts !== null && !mccOnly && accounts.length === 0 && (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-2xl border bg-muted/30 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                We couldn't find any Google Ads accounts you manage. If you don't have one, go back
                and check "I don't run Google Ads".
              </div>
            </div>
            <Button type="button" variant="outline" onClick={() => navigate('/onboarding/connect', { replace: true })}>
              Back to Connect
            </Button>
          </div>
        )}

        {accounts !== null && !mccOnly && accounts.length > 0 && (
          <>
            <div className="space-y-3">
              {accounts.map((acc) => {
                const isSelected = selected === acc.customer_id;
                return (
                  <button
                    key={acc.customer_id}
                    type="button"
                    onClick={() => setSelected(acc.customer_id)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition-all',
                      isSelected
                        ? 'border-dark/40 bg-card shadow-card'
                        : 'border-dark/10 bg-card hover:bg-bg-tint',
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-dark">{acc.name}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        ID {acc.customer_id}
                        {acc.currency && ` · ${acc.currency}`}
                        {acc.status && (
                          <>
                            {' · '}
                            {acc.status === 'ENABLED' ? (
                              <span className="inline-flex items-center gap-0.5 align-middle text-green-700">
                                <Check className="h-3 w-3 ml-0.5" />
                                enabled
                              </span>
                            ) : (
                              acc.status.toLowerCase()
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {isSelected && (
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                        <Check className="h-3.5 w-3.5 text-green-700" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {submitError && (
              <Banner variant="error">
                <span>{submitError}</span>
              </Banner>
            )}

            <Button
              type="button"
              className="w-full rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold"
              disabled={!selected || submitting}
              onClick={handleContinue}
            >
              {submitting ? 'saving…' : 'Continue'}
            </Button>
          </>
        )}
      </div>
    </OnboardingShell>
  );
}
